import { TENANT_IDS } from "../constants/tenantIds";

type FixedTenant = { id: string; name: string };

export type WastePipelineFileSlot = {
  /** Unique key used to map files end-to-end (registry → S3 → local path → pipeline run) */
  key: string;
  /** Display label shown on the UI upload button */
  label: string;
  /** HTML accept attribute for the file input (e.g. ".xlsx,.xls,.csv") */
  accept: string;
  /** Whether this file must be provided to dispatch the pipeline */
  required: boolean;
};

export type WastePipelineConfig = {
  /** Display label for UI dropdown */
  label: string;
  /** File upload slots — each slot becomes a separate upload in the UI */
  files: WastePipelineFileSlot[];
  /** If set, pipeline is locked to this tenant (skips org picker in UI) */
  fixedTenant: FixedTenant | null;
  /**
   * Optional set of slot keys where the run must supply at least ONE of
   * them (independent of each slot's individual `required` flag). Used
   * when none of the slots are strictly required on their own but the
   * pipeline still needs at least one file. UI and resolver both enforce
   * this.
   */
  requireAtLeastOneOf?: readonly string[];
  /**
   * Tenant IDs that have pipeline-specific custom rules baked into the
   * transforms (e.g. an anomaly check that only fires for one tenant, or a
   * tenant-pending site that's rejected only when that tenant runs it).
   * The dispatch UI uses this to show a "Custom rules apply for this
   * tenant" indicator when the active org is in the list, so staff know to
   * expect divergent behavior vs the vendor-generic baseline.
   *
   * Kept intentionally minimal — just a flag. Detail on *which* rules apply
   * lives in the pipeline's spec/README, not here.
   */
  tenantsWithCustomRules?: readonly string[];
};

export const WASTE_PIPELINE_REGISTRY = {
  "amazon-clean-earth": {
    label: "Amazon Clean Earth",
    files: [
      {
        key: "source",
        label: "CE Monthly Reporting Package",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.AMAZON, name: "Amazon" },
  },
  "amazon-clean-harbors": {
    label: "Amazon Clean Harbors",
    files: [
      { key: "biennial", label: "Biennial Data", accept: ".xlsx,.xls", required: true },
      { key: "monthly", label: "Monthly Data (Optional)", accept: ".xlsx,.xls", required: false },
    ],
    fixedTenant: { id: TENANT_IDS.AMAZON, name: "Amazon" },
  },
  "aspirus-stericycle": {
    label: "Aspirus Stericycle",
    files: [
      {
        key: "source",
        label: "Stericycle Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.ASPIRUS, name: "Aspirus" },
  },
  "phoenix-childrens-stericycle": {
    label: "Phoenix Children's Stericycle",
    files: [
      {
        key: "source",
        label: "Stericycle Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: {
      id: TENANT_IDS.PHOENIX_CHILDREN_S_HOSPITAL,
      name: "Phoenix Children's Hospital",
    },
  },
  "safety-kleen": {
    label: "Safety-Kleen",
    files: [
      {
        key: "source",
        label: "SK Waste Tracking Summary (.xls)",
        accept: ".xls,.xlsx",
        required: true,
      },
    ],
    fixedTenant: null,
    // Tenants with tenant-specific Safety-Kleen rules:
    //   - UPS: TX Class 1 non-industrial anomaly (Section 3.5) and the
    //     pending KYR000047704 Superfund-site rejection (Appendix A.1).
    //   - AEG: per-state tagging rules (LIB in MI, Drained Used Oil
    //     Filters / Oily Wastes / Used Oil in FL) per ENG-15965; rules
    //     live under `apps/lambdas/data-pipeline/src/tenants/aeg/waste/safety-kleen/`.
    // The dispatch UI surfaces a "Custom rules apply" indicator when one
    // of these tenants is selected so reviewers expect divergent behavior.
    tenantsWithCustomRules: [TENANT_IDS.UPS, TENANT_IDS.ATLA_EQUIPMENT_GROUP],
  },
  "knauf-waste-management": {
    label: "Knauf Waste Management",
    // The WM workbook is the transactional source; the Profile List is the
    // authoritative Path-1 lookup (description/TSDF/method/tags) and MUST be
    // re-uploaded each run so it stays current (SME decision B1).
    files: [
      {
        key: "source",
        label: "WM Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
      {
        key: "profileList",
        label: "Knauf WM Profile List",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "knauf-arcwood": {
    label: "Knauf Arcwood",
    // The Arcwood portal's Shipping History download is an HTML table saved
    // with an .xls extension — upload the raw portal file as-is (Excel's
    // "file seems corrupt" warning on manual open is expected).
    files: [
      {
        key: "source",
        label: "Arcwood Shipping History (WS_SHIP_RPT.xls)",
        accept: ".xls,.html",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "clean-harbors": {
    label: "Clean Harbors",
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: false,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: false },
      {
        key: "weeklyXlsx",
        label: "Weekly Tracking File (xlsx/xlsm)",
        accept: ".xlsx,.xlsm",
        required: false,
      },
    ],
    fixedTenant: null,
  },
  "ups-veolia": {
    label: "UPS Veolia",
    // Spec §1.1 lists two files per production run (one haz, one non-haz),
    // but both have the same `WasteGenMgtFlat` schema and downstream
    // transforms are identical. Neither slot is strictly required on its
    // own — `requireAtLeastOneOf` enforces that the run must still supply
    // at least one of them. UI and resolver both honor this rule.
    files: [
      {
        key: "hazardous",
        label: "Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "nonHazardous",
        label: "Non-Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["hazardous", "nonHazardous"],
    fixedTenant: { id: TENANT_IDS.UPS, name: "UPS" },
  },
  "ups-world-oil": {
    label: "UPS World Oil",
    files: [
      {
        key: "source",
        label: "World Oil Activity Report (.xlsx)",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.UPS, name: "UPS" },
  },
} as const satisfies Record<string, WastePipelineConfig>;

export type WastePipelineName = keyof typeof WASTE_PIPELINE_REGISTRY;

export function isWastePipelineName(name: string): name is WastePipelineName {
  return name in WASTE_PIPELINE_REGISTRY;
}

export const WASTE_PIPELINE_NAMES =
  Object.keys(WASTE_PIPELINE_REGISTRY).filter(isWastePipelineName);

/**
 * Widening accessor for the registry. `WASTE_PIPELINE_REGISTRY` is declared
 * `as const satisfies Record<...>`, which preserves literal types for fields
 * like `fixedTenant` but drops optional fields that aren't set on every
 * pipeline (e.g. `requireAtLeastOneOf`). Reading through this helper gives
 * the consumer the full `WastePipelineConfig` shape without an `as` cast.
 */
export function getWastePipelineConfig(name: WastePipelineName): WastePipelineConfig {
  return WASTE_PIPELINE_REGISTRY[name];
}
